import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { GxTabGroup } from '@garpix/garpix-web-components-react';
import { useStoreon } from 'storeon/react';
import cn from 'classnames';
import dayjs from 'dayjs';

import { api } from '@/api';
import { Events, State } from '@/interfaces';
import ModalLayout from '../../../../components/ModalLayout';
import ModalUserFooter from './ModalUserFooter';
import ModalUserHeader from './ModalUserHeader';
import ModalUserTabNavs from './ModalUserTabNavs';
import ModalOrder from '../ModalOrder';
import { AddressPanel, PayOrderPanel, CertificatesPanel, DataPanel, DocumentsPanel, StatisticsPanel, } from './ModalUserTabPanels';
import DepositsPanel from './ModalUserTabPanels/DepositsPanel';
import { modalUserSchema } from '../../schema';
import { createUser, initialValueUserModal, updateUser } from '../../helpers';
import { Context } from './Context';
import useModalFlag from '../../../../hooks/useModalFlag';
import { TYPE_USER } from '../../../../const';

import { IInitialValues, IModalUser } from './types';
import styles from './styles/index.module.scss';
import 'react-responsive-modal/styles.css';

const ModalUser: FC<IModalUser> = ({
  flag,
  onRequestClose,
  props,
  onChangeAfterCreateUser,
  setParticipant,
  setIsCreateParticipant,
  isCreateParticipant
}) => {
  const [userData, setUserData] = useState<any>();
  const [values, setValues] = useState<any>(initialValueUserModal());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { dispatch, currentUser, selectedUser } = useStoreon<State, Events>(
    'currentUser',
    'selectedUser'
  );


  const { isEdit, userId, user } = props;
  const getUserData = async (id: number) => {
    if (isEdit && id) {
      const data = await api.user.getUser(id);
      setUserData(data);
      setValues(initialValueUserModal(data));
      if (currentUser.user_status !== TYPE_USER.VIEWER) {
        dispatch('depositListUser/get', { user_id: id, is_has_resource: true });
      }
    }
  };

  useEffect(() => {
    getUserData(userId);
    if (user) {
      setValues(initialValueUserModal(user));
    }
  }, [userId, isEdit]);

  const SendData = async (data: IInitialValues) => {
    setIsLoading(true);
    
    const { 
      birthday,
      acceptance_date_for_airtube, 
      acceptance_date_for_wave, 
      source_of_traffic, 
      tube_user_type, 
      wave_user_type 
    } = data.user;

    try {
      if (isEdit) {
        await updateUser(userId || user.id, {
          ...data.user,
          birthday: birthday && dayjs(birthday, 'DD-MM-YYYY').format('YYYY-MM-DD') || undefined,
          source_of_traffic: source_of_traffic.value,
          tube_user_type: tube_user_type.value,
          wave_user_type: wave_user_type.value,
          acceptance_date_for_airtube: acceptance_date_for_airtube && dayjs(acceptance_date_for_airtube, 'DD-MM-YYYY').format('YYYY-MM-DD') || null,
          acceptance_date_for_wave: acceptance_date_for_wave && dayjs(acceptance_date_for_wave, 'DD-MM-YYYY').format('YYYY-MM-DD') || null
        });
        if (selectedUser?.id === userId || selectedUser?.id === user?.id) {
          const data = await api.user.getUser(userId || user.id);
          await dispatch('selectedUser/set', {
            ...data,
            value: data.id,
            label: `${data.last_name ? data.last_name : ''} ${data.first_name ? data.first_name : ''} ${data.phone ? data.phone : ''}`,
          });
        }
        onRequestClose();
      } else {
        createUser({
          ...data.user,
          acceptance_date_for_airtube: acceptance_date_for_airtube && dayjs(acceptance_date_for_airtube, 'DD-MM-YYYY').format('YYYY-MM-DD') || undefined,
          acceptance_date_for_wave: acceptance_date_for_wave && dayjs(acceptance_date_for_wave, 'DD-MM-YYYY').format('YYYY-MM-DD') || undefined,
          birthday: birthday && dayjs(birthday, 'DD-MM-YYYY').format('YYYY-MM-DD') || undefined,
          source_of_traffic: data.user.source_of_traffic.value,
          tube_user_type: data.user.tube_user_type.value,
          wave_user_type: data.user.wave_user_type.value,
        })
          .then((res) => {
            if (res && res.id && !isCreateParticipant) {
              dispatch('selectedUser/set', {
                ...res,
                value: res.id,
                label: `${res.last_name || ''} ${res.first_name || ''} ${res.phone || ''}`,
              });
            }

            if (isCreateParticipant && setIsCreateParticipant && setParticipant) {
              setParticipant({
                ...res,
                value: res.id,
                label: `${res.last_name || ''} ${res.first_name || ''} ${res.phone || ''}`,
              })
              setIsCreateParticipant(false);
            }

            if (onChangeAfterCreateUser && !isCreateParticipant) {
              onChangeAfterCreateUser(res);
            }
            onRequestClose();
          });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: values,
    onSubmit: (data) => SendData(data),
    validationSchema: modalUserSchema,
    validateOnChange: false,
  });

  const [orderInfo, setOrderInfo] = useState<Record<string, any> | null>({});
  const {
    flag: modalOrderOpen,
    setModalFlag: toggleFlagModalOrder,
  } = useModalFlag();
  const handleClickModal = (
    date: string | null,
    order: Record<string, any> | null
  ) => {
    setOrderInfo({ date, order });
    toggleFlagModalOrder();
  };

  return (
    <Context.Provider value={[handleClickModal, getUserData]}>
      <ModalLayout flag={flag} onClose={onRequestClose}>
        <div style={{ maxWidth: '100%' }}>
          <ModalUserHeader isEdit={isEdit} userData={userData} />
          <GxTabGroup
            className={cn(styles['tabs-group'], {
              [styles['tabs-group__viewer']]: currentUser.user_status === TYPE_USER.VIEWER
            })}>
            <ModalUserTabNavs isEdit={isEdit} onClickDeposit={() => getUserData(userId)} />
            <>
              <DataPanel formik={formik}/>
              <StatisticsPanel userId={userId} />
              {currentUser.user_status !== TYPE_USER.VIEWER && (
                <>
                  <PayOrderPanel userId={userId} />
                  <AddressPanel formik={formik} />
                  <DocumentsPanel formik={formik} />
                  <DepositsPanel userData={userData} />
                  <CertificatesPanel userData={userData} />
                </>
              )}

            </>
          </GxTabGroup>
          <ModalUserFooter
            isLoading={isLoading}
            formik={formik}
            onRequestClose={onRequestClose}
            isEdit={isEdit}
            userData={userData}
          />
        </div>

      </ModalLayout>
      {modalOrderOpen && (
        <ModalOrder
          data={orderInfo?.order}
          date={orderInfo?.date}
          flag={modalOrderOpen}
          closeModal={toggleFlagModalOrder}
          selectedTypeOrder={orderInfo?.order.side}
        />
      )}
    </Context.Provider>
  );
};

export default ModalUser;
